import { useState } from 'react';
import { StatusPanel } from './components/StatusPanel.js';
import { AuditLog } from './components/AuditLog.js';
import { RulesList } from './components/RulesList.js';
import { GeoMap } from './components/GeoMap.js';
import { GeoTable } from './components/GeoTable.js';
import { ProbeHistory } from './components/ProbeHistory.js';
import { GeoSettings } from './components/GeoSettings.js';
import { ScheduleGrid } from './components/ScheduleGrid.js';

type Tab = 'dashboard' | 'rules' | 'geo' | 'scheduling';

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: 'dashboard',  label: 'Dashboard',  icon: '📊' },
  { id: 'rules',      label: 'Rules',      icon: '📋' },
  { id: 'geo',        label: 'Geo',        icon: '🗺️' },
  { id: 'scheduling', label: 'Scheduling', icon: '📅' },
];

export function GuardianPage() {
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');

  return (
    <div className="space-y-6 min-h-full">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-3">
          <span>🛡️</span> Guardian
        </h1>
        <p className="text-zinc-400 mt-1">Automated campaign management — rules engine, geo automation, and scheduling</p>
      </div>

      {/* Tab Bar */}
      <div className="border-b border-slate-800 -mt-2 overflow-x-auto">
        <nav className="flex gap-1 min-w-max" aria-label="Guardian tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-slate-500 hover:text-slate-300 hover:border-slate-600'
                }
              `}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Dashboard Tab ─────────────────────────────────────────── */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          <StatusPanel />
          <AuditLog />
        </div>
      )}

      {/* ── Rules Tab ─────────────────────────────────────────────── */}
      {activeTab === 'rules' && (
        <div className="space-y-6">
          <RulesList />
        </div>
      )}

      {/* ── Geo Tab ───────────────────────────────────────────────── */}
      {activeTab === 'geo' && (
        <div className="space-y-6">
          <GeoMap />
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2">
              <GeoTable />
            </div>
            <div className="space-y-6">
              <GeoSettings />
            </div>
          </div>
          <ProbeHistory />
        </div>
      )}

      {/* ── Scheduling Tab ────────────────────────────────────────── */}
      {activeTab === 'scheduling' && (
        <div className="space-y-6">
          <ScheduleGrid />
        </div>
      )}
    </div>
  );
}
